// frontend/src/admin/v2/adminStatus.js
//
// Die EINE Etikett-Quelle der drei Admin-Kartenlisten (E1 `/admin/jobs`,
// E2 `/admin/secrets`, E3 `/admin/integrations`).
//
// Vorher trug jede Ansicht ihre eigene Abbildung — und alle drei sagten etwas
// anderes fuer denselben Serverzustand:
//
//   E1  Healthy / Watch / Failed
//   E2  Healthy / Stale / Missing
//   E3  Operational / Needs attention / Issue
//
// Der Server liefert in allen drei Antworten DASSELBE Feld `status` mit
// denselben drei Werten (`green`/`amber`/`red`) — es gab also drei Woerter fuer
// eine Lage. Diese Datei ist die eine Stelle, an der die Abbildung steht.
//
// 🔴 Sie ist ein DOMAENEN-MODUL, kein Baustein: sie liegt neben den Ansichten,
// rendert nichts und exportiert reine Funktionen/Konstanten. Deshalb ist sie
// kein `components/common/`-Eintrag und braucht keinen Registry-Eintrag
// (Regel 12 gilt fuer geteilte Bausteine).

/**
 * TON — Statuswert → Theme-Farbpfad.
 *
 * 🔴 Der Pfad, keine Farbe: ein Hex- oder `rgba`-Literal waere ein Wert
 * ausserhalb des Themes (Regel 22), und der Theme-Pfad folgt jeder Palette.
 */
export const TON = {
  green: 'success.main',
  amber: 'warning.main',
  red: 'error.main',
}

const ETIKETT = {
  green: 'Healthy',
  amber: 'Watch',
  red: 'Failing',
}

/**
 * etikett — der sichtbare Text zu einem Serverstatus.
 *
 * @param {'green'|'amber'|'red'|string} status  der Wert aus `data.*.status`
 * @param {object} [optionen]
 * @param {boolean} [optionen.running=false]  E1: ein laufender Job wird als
 *   „Running" benannt, nicht als „Watch". Der Server macht „running" zu `amber`
 *   (`backend/admin_v2/jobs.py:_serialize`), damit die Zaehlzeile stimmt; die
 *   Karte sagt trotzdem, was gerade passiert. Nur E1 kennt diesen Zustand —
 *   E2/E3 rufen ohne das Feld.
 * @param {string} [optionen.red='Failing']  das Wort fuer `red`. E2 setzt
 *   „Missing": dort heisst rot „die Env-Variable fehlt", und „Failing" waere
 *   eine Aussage ueber einen Dienst, den es nicht gibt. Das ist ein WORT, keine
 *   zweite Abbildung — die Zuordnung Status → Wort/Farbe bleibt hier.
 * @returns {string} „Healthy" | „Watch" | „Failing" (bzw. `red`) | „Running"
 *
 * 🔴 Ein unbekannter Status faellt auf „Watch" — dieselbe Vorgabe, die die drei
 * Ansichten vorher je einzeln hatten (`STATUS_TONE[...] || STATUS_TONE.amber`).
 * Er wird NICHT zu „Healthy": ein Wert, den niemand kennt, ist kein gutes
 * Zeichen.
 */
export function etikett(status, { running = false, red = ETIKETT.red } = {}) {
  if (running) return 'Running'
  if (status === 'red') return red
  if (status === 'green') return ETIKETT.green
  return ETIKETT.amber
}

/**
 * zustandswert — der FILTERWERT eines Serverstatus: das Etikett, kleingeschrieben.
 *
 * 🔴 **Die Adresse traegt das WORT, nicht das Servertoken** (Plan §3.1: E2
 * `healthy · watch · missing`, E3 `healthy · watch · failing`, E1 ebenso).
 * Vorher standen hier `green`/`amber`/`red` — dann filterte die dokumentierte
 * Adresse `?status=missing` nichts, und `useListQuery` verwarf den Wert mit
 * einem „Ignored unknown filter value"-Hinweis (R16 C-1).
 *
 * 🔴 **EINE Abbildung, kein zweiter Wortschatz:** der Wert entsteht aus
 * demselben `etikett`, das die Karte rendert. Was auf der Karte steht, steht
 * auch in der Adresse.
 *
 * @param {'green'|'amber'|'red'|string} status
 * @param {{running?: boolean, red?: string}} [optionen] wie bei `etikett`
 * @returns {string} `healthy` | `watch` | `failing` (bzw. das rote Wort)
 */
export function zustandswert(status, optionen = {}) {
  return etikett(status, optionen).toLowerCase()
}

/**
 * ton — der Theme-Farbpfad zu einem Serverstatus (fuer `color`/`borderColor`).
 *
 * @param {'green'|'amber'|'red'|string} status
 * @returns {string} `success.main` | `warning.main` | `error.main`
 */
export function ton(status) {
  return TON[status] || TON.amber
}
